/* global window */

import videojs from 'video.js';
import get from 'lodash/get';

/**
 * Class representing an Amazon bid.
 * Use `window.apstag.debug('enable')` to force bid response
 */
class AmazonBid {
  /**
   * Create an Amazon bid.
   * @param {Object} id - player ID.
   * @param {string} settings - player settings.
   * @param {string} slotId - bid slot ID.
   */
  constructor(id, settings, slotId = 'videoSlot') {
    this.id = id;
    this.settings = settings;
    this.bids = [];
    this.timeout = 2000;
    this.waitTimeout = 2000;
    this.slotId = slotId;
  }

  /**
   * Check if Amazon A9 is loaded.
   * @returns boolean
   */
  get isA9Ready() {
    return typeof (window.apstag) !== 'undefined';
  }

  /**
   * Wait for Amazon A9 to be ready.
   * @param {function} resolve - promise resolve function.
   * @param {integer} startTime - start time.
   * @returns {Promise}
   */
  waitFunc(resolve, startTime) {
    if (this.isA9Ready) {
      resolve(true);
      return;
    }

    if (Date.now() - startTime > this.waitTimeout) {
      resolve(false);
      return;
    }

    setTimeout(this.waitFunc.bind(this, resolve, startTime), 50);
  }

  /**
   * Wait for Amazon A9 to be ready.
   * @returns {Promise}
   */
  waitForA9() {
    const startTime = Date.now();

    return new Promise(resolve => {
      this.waitFunc(resolve, startTime);
    });
  }

  /**
   * Request Amazon bid response.
   * @returns {Promise}
   */
  bid() {
    return new Promise(resolve => {
      if (!this.settings.enableAmazonBids) {
        videojs.log(`${this.id} A9 disabled`);
        resolve();
        return;
      }

      const { imaSdkAvailable, adBlockDetected } = this.settings;
      if (!imaSdkAvailable || adBlockDetected) {
        videojs.log(`${this.id} A9 IMA SDK not available`);
        resolve();
        return;
      }

      if (!this.isA9Ready) {
        videojs.log(`${this.id} A9 not ready`);
        resolve();
        return;
      }

      const slots = [];
      slots.push({
        slotID: this.slotId,
        mediaType: 'video',
      });

      window.apstag.fetchBids({
        slots,
        timeout: this.timeout,
      }, bids => {
        videojs.log(`${this.id} A9 got bids: [${bids.length}]`);

        this.bids = bids;

        resolve();
      });
    });
  }

  /**
   * Request Amazon bid response.
   * @returns {Promise}
   */
  request() {
    if (this.isA9Ready ||
      !this.settings.imaSdkAvailable ||
      !this.settings.enableAmazonBids) {
      return this.bid();
    }

    return new Promise(resolve => {
      this.waitForA9()
        .then(() => resolve(this.bid()));
    });
  }

  /**
   * Return Amazon bid response.
   */
  get scp() {
    let scp = '';

    /*
      Sample bid response:
      [{
        "slotID": "videoSlot",
        "size": "x",
        "mediaType": "video",
        "targeting": {
          "amzniid": "testImpression-3474267985051616629900786",
          "amznbid": "v_testBid",
          "amznp": "testP",
          "amznsz": "x",
          "amzniid_sp": "testImpression-3575206455431616629900786",
          "amznbid_sp": "testBidSP",
          "amznp_sp": "testPSP",
          "amznsz_sp": "x",
          "amzndeals": [
            "testDeal1892015736371616629900786_x"
          ],
          "testDeal1892015736371616629900786_xamzniid": "testDealIi-3077742594431616629900786"
        },
        "helpers": {
          "encodedQsParams": f(),
          "qsParams": f(),
          "targetingKeys": [
            "amzniid",
            "amznbid",
            "amznp",
            "amznsz",
            "amzniid_sp",
            "amznbid_sp",
            "amznp_sp",
            "amznsz_sp",
            "amzndeal_sp",
            "amzndeals",
            "testDeal1892015736371616629900786_xamzniid"
          ]
        }
      }]
    */
    const encodedQsParams = get(this.bids, '0.helpers.encodedQsParams', null);

    if (typeof encodedQsParams === 'function') {
      /*
        Sample encodedQsParams value:
        `"%26amzniid%3DtestImpression-3474267985051616629900786%26amznbid%3Dv_testBid%26amznp%3DtestP%26amznsz%3Dx%26amzniid_sp%3DtestImpression-3575206455431616629900786%26amznbid_sp%3DtestBidSP%26amznp_sp%3DtestPSP%26amznsz_sp%3Dx%26amzndeal_sp%3D%26amzndeals%3DtestDeal1892015736371616629900786_x%26testDeal1892015736371616629900786_xamzniid%3DtestDealIi-3077742594431616629900786"`
      */
      const params = encodedQsParams();

      if (typeof params === 'string' && params.length > 3) {
        scp = params.substring(3, params.length);
      }
    }

    return scp;
  }

  /**
   * Sets bid slot ID.
   * @param {string} slotId bid slot ID.
   */
  setSlotId(slotId) {
    this.slotId = slotId;
  }
}

export default AmazonBid;
